import { useEffect, useState } from 'react';
import { useTranslation } from '@tasama/i18n';
import { OnboardingHeader, DocumentUploadSection } from '@tasama/ui';
import { useFileUploadProgress } from '@tasama/utils';
import {
  useUploadDocumentsStore,
  useUploadDocumentsFormData,
  useUploadDocumentsNetworkError,
  uploadedFileRegistry,
  type UploadDocumentsStore,
} from '../stores/upload-documents.store';
import {
  getValidateUploadDocumentsForm,
  type UploadedFile,
} from '../schemas/upload-documents.schema';

export function UploadDocumentsPage() {
  const { t } = useTranslation('onboarding');
  const validateUploadDocumentsForm = getValidateUploadDocumentsForm(t);
  const [isFileRemoved, setIsFileRemoved] = useState(false);
  const [fileError, setFileError] = useState<string | undefined>();
  const formData = useUploadDocumentsFormData();
  const uploadNetworkError = useUploadDocumentsNetworkError();
  const addFiles = useUploadDocumentsStore(
    (s: UploadDocumentsStore) => s.addFiles
  );
  const updateFile = useUploadDocumentsStore(
    (s: UploadDocumentsStore) => s.updateFile
  );
  const removeFile = useUploadDocumentsStore(
    (s: UploadDocumentsStore) => s.removeFile
  );
  const setValidity = useUploadDocumentsStore(
    (s: UploadDocumentsStore) => s.setValidity
  );
  const setUploadNetworkError = useUploadDocumentsStore(
    (s: UploadDocumentsStore) => s.setUploadNetworkError
  );
  const { startProgress } = useFileUploadProgress();

  // Page owns validity — derived from file list state
  useEffect(() => {
    const valid =
      formData.uploadedFiles.length > 0 &&
      formData.uploadedFiles[0]?.status === 'complete';
    setValidity(valid);
    if (valid) setFileError(undefined);
  }, [formData.uploadedFiles, setValidity]);

  const handleFilesSelected = (files: FileList) => {
    setIsFileRemoved(false);
    setUploadNetworkError(null);
    const fileArray = Array.from(files);

    const newFiles: UploadedFile[] = fileArray.map((file, index) => {
      const id = `file-${Date.now()}-${index}`;
      uploadedFileRegistry.set(id, file);
      return {
        id,
        fileName: file.name,
        fileSize: `${Math.round(file.size / 1024)} KB`,
        bytes: file.size,
        progress: 0,
        status: 'uploading' as const,
      };
    });

    // Pre-validate type/size before starting animation
    const validationResult = validateUploadDocumentsForm({
      uploadedFiles: newFiles,
    });

    if (!validationResult.success) {
      addFiles(newFiles.map((f) => ({ ...f, status: 'error' as const })));
      setFileError(
        validationResult.errors?.uploadedFiles ??
          t('errors.invalidFileSelected')
      );
      setValidity(false);
      return;
    }

    addFiles(newFiles);

    newFiles.forEach((file, index) => {
      startProgress({
        fileId: file.id,
        delayMs: index * 100,
        getLatestProgress: () =>
          useUploadDocumentsStore
            .getState()
            .formData.uploadedFiles.find((f) => f.id === file.id)?.progress,
        onUpdate: (progress, status) =>
          updateFile(file.id, { progress, status }),
        onComplete: () => {
          // useEffect above will pick up status === 'complete' and set validity
        },
      });
    });
  };

  const handleFileRemove = (fileId: string) => {
    setIsFileRemoved(true);
    uploadedFileRegistry.delete(fileId);
    removeFile(fileId);
    setFileError(undefined);
    // useEffect above will set isValid = false when uploadedFiles empties
  };

  const isDocumentCompleted = formData.uploadedFiles.some(
    (file) => file.status === 'complete'
  );

  return (
    <div className="space-y-8">
      <OnboardingHeader
        icon="Upload"
        size="lg"
        title={t('uploadDocuments.title')}
        description={t('uploadDocuments.subtitle')}
      />

      <DocumentUploadSection
        label={t('uploadDocuments.fields.registrationCertificate.label')}
        isCompleted={isDocumentCompleted}
        files={formData.uploadedFiles}
        onFilesSelected={handleFilesSelected}
        onFileRemove={handleFileRemove}
        isFileRemoved={isFileRemoved}
        testID="company-registration-upload"
        errorMessage={uploadNetworkError ?? fileError}
        supportingText={t(
          'uploadDocuments.fields.registrationCertificate.supportingText'
        )}
      />
    </div>
  );
}
